import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ServiceOffer, CartItem} from '../../../../data/model';
import {NavigationEventService} from '../../../navigation-event.service';
import {ServiceOfferFormComponent} from '../service-offer-form';
import {ContextData} from '../../../../common/context-data';

interface ServiceOfferRadioForm {
  addItemToCart: FormControl<boolean | null>;
  marketingOptIn?: FormControl<boolean>;
}

@Component({
  selector: 'app-radio-form',
  templateUrl: './radio-form.component.html',
  styleUrls: ['./radio-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // JMB [2023-05-12]: Breaking with convention so that the styles don't overlap with the old.
  host: {class: 'app-radio-form-component'},
  standalone: false,
})
export class RadioFormComponent
  extends ServiceOfferFormComponent
  implements OnChanges
{
  @Input() serviceOffer: ServiceOffer;
  @Input() contextData: ContextData;
  @ViewChild('radioErrorMessage') radioErrorEle: ElementRef;

  continueClicked = false;
  offerForm: FormGroup<ServiceOfferRadioForm> =
    new FormGroup<ServiceOfferRadioForm>({
      addItemToCart: new FormControl<boolean | null>(null, [
        Validators.required,
      ]),
    });

  constructor(
    protected ref: ChangeDetectorRef,
    private continueEvent: NavigationEventService,
  ) {
    super(ref);
    this.continueEvent.continueClick.subscribe(() => {
      this.continueClicked = true;
      this.ref.detectChanges();
    });
  }

  get yesRadioLabel(): string {
    let result = String(this.getControlLabel('yes'));
    const signup = this.contextData?.signups[0];
    if (result.includes('${firstName}') && signup?.participant?.firstName) {
      result = result.replaceAll(
        /\${firstName}/g,
        signup?.participant.firstName,
      );
    }
    return result;
  }

  get noRadioLabel(): string {
    return this.getControlLabel('no') || '';
  }

  get marketingOptInLabel(): string {
    return this.serviceOffer?.marketingLabel || '';
  }

  get showMarketingOptIn(): boolean {
    return !!this.serviceOffer?.marketingLabel;
  }

  get addItemToCartRadio(): FormControl<boolean> {
    return this.offerForm.get('addItemToCart') as FormControl<boolean>;
  }

  get marketingCheckOptions(): Array<{label: string; value: boolean}> {
    return [{label: this.marketingOptInLabel, value: true}];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serviceOffer']) {
      this.syncMarketingOptInControl();
    }
  }

  addItemToCartHasError(): boolean {
    return this.addItemToCartRadio.errors?.['required'] && this.continueClicked;
  }

  addonSelected(): boolean {
    return !!this.offerForm.value.addItemToCart;
  }

  bindFormValue(selected: boolean): void {
    this.offerForm.patchValue({
      addItemToCart: selected || null,
    });
    this.ref.detectChanges();
  }

  valid(): boolean {
    return super.valid();
  }

  value(): CartItem {
    const item = super.value();
    if (this.showMarketingOptIn) {
      item.marketingOptIn = !!this.offerForm.get('marketingOptIn')?.value;
    }
    return item;
  }

  private getControlLabel(yesOrNo: 'yes' | 'no'): string | undefined {
    const labels = this.serviceOffer?.inputLabels || [];
    if (!labels.length) {
      return undefined;
    }

    const noMatcher = /\b(no|pass)\b/i;
    const yesMatcher = /\b(yes|add)\b/i;

    if (yesOrNo === 'no') {
      return labels.find(label => noMatcher.test(label));
    }

    return (
      labels.find(label => yesMatcher.test(label)) ||
      labels.find(label => !noMatcher.test(label)) ||
      labels[0]
    );
  }

  private syncMarketingOptInControl(): void {
    const control = this.offerForm.get('marketingOptIn');
    if (this.showMarketingOptIn && !control) {
      this.offerForm.addControl(
        'marketingOptIn',
        new FormControl<boolean>(false, {nonNullable: true}),
      );
      return;
    }

    if (!this.showMarketingOptIn && control) {
      this.offerForm.removeControl('marketingOptIn');
    }
  }
}
